<div
  #videoContainer
  class="video-container"
  [class.controls-visible]="controlsVisible()"
  [class.is-paused]="!isPlaying()"
  (mousemove)="onMouseMove()"
  (mouseleave)="onMouseLeave()"
  (focusin)="onFocusIn()"
  (focusout)="onFocusOut($event)">

  <!-- Video element -->
  @if (activeStream()) {
    <video
      #videoPlayer
      [src]="activeStream()!.url"
      [poster]="posterUrl()"
      (play)="onPlay()"
      (pause)="onPause()"
      (timeupdate)="onTimeUpdate()"
      (loadedmetadata)="onLoadedMetadata()"
      (waiting)="onWaiting()"
      (canplay)="onCanPlay()"
      (ended)="onEnded()"
      (click)="togglePlay()">
    </video>
  }

  <!-- Frame-anchor overlay: sized to the VIDEO CONTENT box (letterbox-aware), so pins,
       regions, and the annotate gesture all use plain fractions of the FRAME. Pins render
       only around their own moment; the gesture layer takes the pointer only while
       annotate mode is armed (arming pauses playback). -->
  @if (videoBox(); as box) {
    <div class="video-frame-overlay"
         [class.annotating]="annotateMode()"
         [style.left.px]="box.left" [style.top.px]="box.top"
         [style.width.px]="box.width" [style.height.px]="box.height"
         (pointerdown)="onFramePointerDown($event)"
         (pointermove)="onFramePointerMove($event)"
         (pointerup)="onFramePointerUp($event)"
         (pointercancel)="onFramePointerCancel()">
      @for (pin of visibleFramePins(); track pin.id) {
        @if (pin.width != null && pin.height != null) {
          <div class="frame-region"
               [style.left.%]="pin.x * 100" [style.top.%]="pin.y * 100"
               [style.width.%]="pin.width * 100" [style.height.%]="pin.height * 100"></div>
        }
        <button type="button" class="frame-pin"
                [class.active]="activePinId() === pin.id"
                [style.left.%]="pin.x * 100" [style.top.%]="pin.y * 100"
                (click)="framePinClick.emit(pin.id)">{{ pin.ordinal }}</button>
      }
      @if (frameDraftRect(); as draft) {
        <div class="frame-draft"
             [style.left.%]="draft.left" [style.top.%]="draft.top"
             [style.width.%]="draft.width" [style.height.%]="draft.height"></div>
      }
      <!-- The captured-but-not-yet-posted mark stays visible while the comment is
           written (draft-styled, unnumbered); it clears with the composer's chip. -->
      @if (pendingFramePin(); as pending) {
        @if (pending.width != null && pending.height != null) {
          <div class="frame-region frame-region--pending"
               [style.left.%]="pending.x * 100" [style.top.%]="pending.y * 100"
               [style.width.%]="pending.width * 100" [style.height.%]="pending.height * 100"></div>
        }
        <span class="frame-pin frame-pin--pending"
              [style.left.%]="pending.x * 100" [style.top.%]="pending.y * 100">
          <fa-duotone-icon [icon]="faLocationDot"></fa-duotone-icon>
        </span>
      }
    </div>
  }

  <!-- Buffering spinner -->
  @if (isBuffering()) {
    <div class="video-buffering">
      <p-progressSpinner strokeWidth="3"></p-progressSpinner>
    </div>
  }

  <!-- Center play button — hidden while annotating: the paused frame is the canvas and
       the big play target would sit over the gesture layer stealing its clicks. -->
  @if (!isPlaying() && controlsVisible() && !annotateMode()) {
    <button class="video-center-play" (click)="togglePlay()">
      <fa-duotone-icon [icon]="faPlay"></fa-duotone-icon>
    </button>
  }

  <!-- Custom control bar -->
  <div class="video-controls" [class.visible]="controlsVisible()">
    <!-- Progress bar: a slider for the keyboard — ← / → step 5 s, Home / End the ends. The keys
         are consumed here so the preview's own ← / → item walk never sees them. -->
    <div class="video-progress-container focus-ring"
         role="slider" tabindex="0"
         [attr.aria-label]="'filePreview.position' | translate"
         aria-valuemin="0" [attr.aria-valuemax]="duration()" [attr.aria-valuenow]="currentTime()"
         [attr.aria-valuetext]="currentTimeFormatted()"
         (click)="seek($event)"
         (keydown.arrowleft)="$event.preventDefault(); $event.stopPropagation(); seekBy(-5)"
         (keydown.arrowright)="$event.preventDefault(); $event.stopPropagation(); seekBy(5)"
         (keydown.home)="$event.preventDefault(); $event.stopPropagation(); seekTo(0)"
         (keydown.end)="$event.preventDefault(); $event.stopPropagation(); seekTo(duration())">
      <div class="video-progress-bar">
        <div class="video-progress-fill" [style.width.%]="progress()"></div>
        <!-- Comment-anchor markers: single instants as dots, ranges as strips. Positioned by
             fraction of the duration; ranges RENDER (range capture is a later gesture). -->
        @for (marker of markers(); track marker.id) {
          @if (marker.timecodeTo != null) {
            <span class="video-timecode-marker video-timecode-marker--range"
                  [style.left]="(markerFraction(marker.timecode) * 100) + '%'"
                  [style.width]="((markerFraction(marker.timecodeTo) - markerFraction(marker.timecode)) * 100) + '%'"></span>
          } @else {
            <span class="video-timecode-marker"
                  [style.left]="(markerFraction(marker.timecode) * 100) + '%'"></span>
          }
        }
      </div>
    </div>

    <!-- Controls row -->
    <div class="video-controls-row">
      <!-- Left: Play/Pause + Time -->
      <div class="video-controls-left">
        <button class="video-control-btn" (click)="togglePlay()" [pTooltip]="(isPlaying() ? 'filePreview.pause' : 'filePreview.play') | translate">
          <fa-duotone-icon [icon]="isPlaying() ? faPause : faPlay"></fa-duotone-icon>
        </button>
        <span class="video-time">
          {{ currentTimeFormatted() }} / {{ durationFormatted() }}
        </span>
        <!-- The former "comment at this moment" button retired: every comment now stamps
             the playhead by default (removable chip in the composer), so an explicit
             capture control would be a second door to the same act. -->
      </div>

      <!-- Right: Volume + Quality + Fullscreen -->
      <div class="video-controls-right">
        <!-- Volume -->
        <div class="video-volume">
          <button class="video-control-btn" (click)="toggleMute()" [pTooltip]="(isMuted() ? 'filePreview.unmute' : 'filePreview.mute') | translate">
            <fa-duotone-icon [icon]="volumeIcon()"></fa-duotone-icon>
          </button>
          <input
            type="range"
            class="video-volume-slider"
            min="0"
            max="1"
            step="0.05"
            [value]="isMuted() ? 0 : volume()"
            (input)="setVolume($event)" />
        </div>

        <!-- Quality switcher -->
        @if (hasMultipleStreams()) {
          <div class="video-quality">
            <button class="video-control-btn quality-btn" (click)="toggleQualityMenu()" [pTooltip]="'filePreview.quality' | translate">
              <span class="quality-label">{{ activeStream()?.label }}</span>
            </button>
            @if (qualityMenuOpen()) {
              <div class="quality-menu">
                @for (stream of videoData().streams; track stream.height) {
                  <button
                    class="quality-option"
                    [class.active]="stream === activeStream()"
                    (click)="selectStream(stream)">
                    {{ stream.label }}
                  </button>
                }
              </div>
            }
          </div>
        }

        <!-- Fullscreen -->
        <button class="video-control-btn" (click)="toggleFullscreen()" [pTooltip]="'filePreview.fullscreen' | translate">
          <fa-duotone-icon [icon]="faExpand"></fa-duotone-icon>
        </button>
      </div>
    </div>
  </div>
</div>
